import { ErrorHandler, Inject, Injectable, Injector } from '@angular/core';
import { WA_WINDOW } from '@ng-web-apis/common';
import * as Sentry from '@sentry/angular';
import { ErrorParserService } from '../error-parser/error-parser.service';
import { NotificationsService } from '../notifications/notifications.service';

const LOGTAG = '[GlobalErrorHandlerService]';

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerService
  extends Sentry.SentryErrorHandler
  implements ErrorHandler
{
  constructor(
    private readonly injector: Injector,
    @Inject(WA_WINDOW) private readonly window: Window,
  ) {
    super();
  }

  /**
   * Handle an unknown error.
   * An error notification is shown.
   */
  public override handleError(error: unknown): void {
    void this.handle(error);
  }

  private async handle(error: unknown): Promise<void> {
    try {
      console.error(error);
      const notificationsService: NotificationsService =
        this.injector.get<NotificationsService>(NotificationsService);
      const message = ErrorParserService.getMessageFromUnknownError(error);
      if (
        /Loading chunk \d+ failed/.test(message) ||
        message.includes('error loading dynamically imported module') ||
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('Importing a module script failed')
      ) {
        // Reload the page if a chunk failed to load
        this.window.location.reload();
        return;
      }
      // Show an error notification
      await notificationsService.showErrorNotification(message);
      // Send the error to Sentry
      super.handleError(error);
    } catch (errorHandlerError) {
      // Log the error to the console
      console.error(`${LOGTAG} Internal exception:`, errorHandlerError);
      // Send the error to Sentry
      super.handleError(errorHandlerError);
    }
  }
}
